import { PROPOSAL_CATEGORIES, PROPOSAL_LOCATIONS } from '@/config/proposal-options';
import type { Proposal } from '@/lib/sse/sse-events';
import type { AiCreateReadyStatus, AiErrorCode, ProposalFormState } from '@/stores/proposal-form';
import { useUploadTrackingStore } from '@/stores/upload-tracking';

type ProposalProgressScalar = string | number | boolean | null;
export type ProposalProgressEventData =
  | ProposalProgressScalar
  | Proposal
  | { [key: string]: ProposalProgressEventData }
  | readonly ProposalProgressEventData[];

export type ProposalProgressPhase =
  | 'idle'
  | 'starting'
  | 'writingMarket'
  | 'findingImages'
  | 'finalizingImages'
  | 'cancelling'
  | 'readyToCreate'
  | 'finalizationFailed';

export interface ProposalJobSnapshot {
  jobId: string;
  userPubkey: string;
  status:
    | 'queued'
    | 'running'
    | 'draft_visible'
    | 'finalizing_images'
    | 'create_ready'
    | 'cancel_requested'
    | 'cancelled'
    | 'finalization_failed'
    | 'failed';
  lastSeq: number;
  createdAt: string;
  updatedAt: string;
  text: {
    title?: string;
    marketTicker?: string;
    category?: string;
    location?: string;
    description?: string;
    outcomes?: { label: string; ticker: string }[];
  };
  images: {
    expected: number;
    previewsSettled: number;
    finalized: number;
    failed: number;
  };
  proposal?: Proposal;
  classification?: { kind: 'opinion' | 'prediction'; confidence: number };
  error?: { stage: string; message: string; retryable: boolean; code?: string };
}

export interface SequencedProposalProgressEvent {
  event: string;
  data: ProposalProgressEventData;
  seq?: number;
  emittedAt?: string;
}

export interface ProposalProgressNotifications {
  onCoverPinFailed?: () => void;
  onOutcomePinFailed?: (index: number) => void;
}

function resolveCategoryName(raw: string): string {
  const v = raw.trim();
  if (v.length === 0) return v;
  const byId = PROPOSAL_CATEGORIES.find((c) => c.id.toLowerCase() === v.toLowerCase());
  if (byId !== undefined) return byId.name;
  const byName = PROPOSAL_CATEGORIES.find((c) => c.name.toLowerCase() === v.toLowerCase());
  return byName?.name ?? v;
}

function resolveLocationName(raw: string): string {
  const v = raw.trim();
  if (v.length === 0) return v;
  const byId = PROPOSAL_LOCATIONS.find((l) => l.id.toLowerCase() === v.toLowerCase());
  if (byId !== undefined) return byId.name;
  const byName = PROPOSAL_LOCATIONS.find((l) => l.name.toLowerCase() === v.toLowerCase());
  return byName?.name ?? v;
}

function statusToCreateReadyStatus(status: ProposalJobSnapshot['status']): AiCreateReadyStatus {
  if (status === 'create_ready') return 'ready';
  if (status === 'cancelled') return 'cancelled';
  if (status === 'failed' || status === 'finalization_failed') return 'failed';
  return 'waiting';
}

function terminalImageSettledCount(images: ProposalJobSnapshot['images']): number {
  return Math.min(images.expected, images.finalized + images.failed);
}

function normalizeAiErrorCode(
  code: ProposalProgressEventData | undefined,
): AiErrorCode | undefined {
  if (code === 'network' || code === 'rate_limited' || code === 'ai_market_content_policy') {
    return code;
  }
  return undefined;
}

function isRecord(
  value: ProposalProgressEventData,
): value is Record<string, ProposalProgressEventData> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isProposalOutcome(value: ProposalProgressEventData): boolean {
  return (
    isRecord(value) &&
    typeof value.label === 'string' &&
    typeof value.ticker === 'string' &&
    (typeof value.imageUrl === 'string' || value.imageUrl === null) &&
    typeof value.imageQuery === 'string'
  );
}

function isProposal(value: ProposalProgressEventData): value is Proposal {
  return (
    isRecord(value) &&
    typeof value.title === 'string' &&
    typeof value.marketTicker === 'string' &&
    typeof value.category === 'string' &&
    typeof value.location === 'string' &&
    typeof value.description === 'string' &&
    Array.isArray(value.outcomes) &&
    value.outcomes.every(isProposalOutcome) &&
    (typeof value.coverImageUrl === 'string' || value.coverImageUrl === null) &&
    typeof value.coverImageQuery === 'string'
  );
}

function readProposal(data: ProposalProgressEventData): Proposal | null {
  if (!isRecord(data)) return null;
  const proposal = data.proposal;
  return proposal !== undefined && isProposal(proposal) ? proposal : null;
}

function hasCompleteImageUrls(proposal: Proposal): boolean {
  return (
    proposal.coverImageUrl !== null &&
    proposal.outcomes.every((outcome) => outcome.imageUrl !== null)
  );
}

function trackPinnedImageForModeration(input: { url: string; sha256?: string }): void {
  if (input.sha256 === undefined || input.sha256.length === 0) return;
  useUploadTrackingStore.getState().track(input.url, input.sha256, 'clean');
}

function applyProposal(state: ProposalFormState, proposal: Proposal): void {
  state.updateField('title', proposal.title);
  state.updateField('marketTicker', proposal.marketTicker);
  state.updateField('category', resolveCategoryName(proposal.category));
  state.updateField('location', resolveLocationName(proposal.location));
  state.updateField('description', proposal.description);
  state.replaceOutcomesFromAi(
    proposal.outcomes.map((outcome) => ({ label: outcome.label, ticker: outcome.ticker })),
    {},
  );
  state.setAiMarketKind(proposal.classification?.kind === 'prediction' ? 'prediction' : 'opinion');
  if (proposal.coverImageUrl !== null) {
    state.setCoverImagePinned(proposal.coverImageUrl);
  }
  for (let index = 0; index < proposal.outcomes.length; index++) {
    const outcome = proposal.outcomes[index];
    if (outcome?.imageUrl !== undefined && outcome.imageUrl !== null) {
      state.setOutcomeImagePinned(index, outcome.imageUrl);
    }
  }
}

export function applyProposalJobSnapshot(
  state: ProposalFormState,
  snapshot: ProposalJobSnapshot,
): void {
  state.setAiJobId(snapshot.jobId);
  state.setAiLastSeq(snapshot.lastSeq);
  state.setAiImagesExpected(snapshot.images.expected);
  state.setAiImagesSettled(terminalImageSettledCount(snapshot.images));
  state.setAiCreateReadyStatus(statusToCreateReadyStatus(snapshot.status));
  state.setAiCancelRequested(
    snapshot.status === 'cancel_requested' || snapshot.status === 'cancelled',
  );

  if (snapshot.text.title !== undefined) state.updateField('title', snapshot.text.title);
  if (snapshot.text.marketTicker !== undefined) {
    state.updateField('marketTicker', snapshot.text.marketTicker);
  }
  if (snapshot.text.category !== undefined) {
    state.updateField('category', resolveCategoryName(snapshot.text.category));
  }
  if (snapshot.text.location !== undefined) {
    state.updateField('location', resolveLocationName(snapshot.text.location));
  }
  if (snapshot.text.description !== undefined) {
    state.updateField('description', snapshot.text.description);
  }
  if (snapshot.text.outcomes !== undefined) {
    state.replaceOutcomesFromAi(snapshot.text.outcomes, {});
  }
  if (snapshot.classification !== undefined) {
    state.setAiMarketKind(snapshot.classification.kind);
  }
  if (snapshot.proposal !== undefined) {
    applyProposal(state, snapshot.proposal);
  }
  if (snapshot.error !== undefined) {
    const code = normalizeAiErrorCode(snapshot.error.code);
    state.setAiError({
      stage: snapshot.error.stage,
      message: snapshot.error.message,
      retryable: snapshot.error.retryable,
      ...(code !== undefined ? { code } : {}),
    });
  }
}

export function applyProposalProgressEvent(
  state: ProposalFormState,
  event: SequencedProposalProgressEvent,
  notifications: ProposalProgressNotifications = {},
): void {
  if (event.seq !== undefined) state.setAiLastSeq(event.seq);

  switch (event.event) {
    case 'job.created':
    case 'job.started':
    case 'started':
    case 'context':
      state.setAiCreateReadyStatus('waiting');
      return;
    case 'title.done': {
      const data = event.data as {
        title: string;
        category: string;
        location: string;
        classification?: { kind: 'opinion' | 'prediction'; confidence: number };
      };
      state.updateField('title', data.title);
      state.updateField('category', resolveCategoryName(data.category));
      state.updateField('location', resolveLocationName(data.location));
      if (data.classification !== undefined) state.setAiMarketKind(data.classification.kind);
      return;
    }
    case 'description.done': {
      const data = event.data as { description: string };
      state.updateField('description', data.description);
      return;
    }
    case 'outcomes.done': {
      const data = event.data as {
        marketTicker: string;
        outcomes: { label: string; ticker: string }[];
      };
      state.updateField('marketTicker', data.marketTicker);
      state.replaceOutcomesFromAi(data.outcomes, {});
      return;
    }
    case 'imageQueries.done': {
      const data = event.data as { outcomeQueries: string[] };
      state.setAiImagesExpected(1 + data.outcomeQueries.length);
      return;
    }
    case 'cover.locked': {
      return;
    }
    case 'cover.pinned': {
      const data = event.data as { url: string; sha256?: string };
      trackPinnedImageForModeration(data);
      state.setCoverImagePinned(data.url);
      state.incrementAiImagesSettled();
      return;
    }
    case 'cover.pin_failed':
      state.setCoverImagePinFailed();
      state.incrementAiImagesSettled();
      state.setAiCreateReadyStatus('failed');
      notifications.onCoverPinFailed?.();
      return;
    case 'cover.failed':
      state.setCoverImage('', false);
      state.incrementAiImagesSettled();
      state.setAiCreateReadyStatus('failed');
      return;
    case 'outcome.locked': {
      return;
    }
    case 'outcome.pinned': {
      const data = event.data as { index: number; url: string; sha256?: string };
      trackPinnedImageForModeration(data);
      state.setOutcomeImagePinned(data.index, data.url);
      state.incrementAiImagesSettled();
      return;
    }
    case 'outcome.pin_failed': {
      const data = event.data as { index: number };
      state.setOutcomeImagePinFailed(data.index);
      state.incrementAiImagesSettled();
      state.setAiCreateReadyStatus('failed');
      notifications.onOutcomePinFailed?.(data.index);
      return;
    }
    case 'outcome.failed': {
      const data = event.data as { index: number };
      state.setOutcomeImage(data.index, '', false);
      state.incrementAiImagesSettled();
      state.setAiCreateReadyStatus('failed');
      return;
    }
    case 'create_ready': {
      const proposal = readProposal(event.data);
      if (proposal === null || !hasCompleteImageUrls(proposal)) {
        state.setAiCreateReadyStatus('failed');
        return;
      }
      applyProposal(state, proposal);
      state.setAiCreateReadyStatus('ready');
      return;
    }
    case 'cancel_requested':
      state.setAiCancelRequested(true);
      return;
    case 'cancelled':
      state.setAiCancelRequested(false);
      state.setAiCreateReadyStatus('cancelled');
      state.clearInFlightImageUploads();
      return;
    case 'done': {
      const proposal = readProposal(event.data);
      if (proposal !== null && hasCompleteImageUrls(proposal)) applyProposal(state, proposal);
      return;
    }
    case 'error': {
      const data = event.data as {
        stage: string;
        message: string;
        retryable: boolean;
        code?: string;
      };
      const code = normalizeAiErrorCode(data.code);
      state.setAiError({
        stage: data.stage,
        message: data.message,
        retryable: data.retryable,
        ...(code !== undefined ? { code } : {}),
      });
      state.setAiCreateReadyStatus('failed');
      state.clearInFlightImageUploads();
      return;
    }
    default:
      return;
  }
}

export function deriveAiProgressPhase(state: ProposalFormState): ProposalProgressPhase {
  if (state.aiCancelRequested) return 'cancelling';
  if (state.aiCreateReadyStatus === 'ready') return 'readyToCreate';
  if (state.aiCreateReadyStatus === 'failed') return 'finalizationFailed';
  if (state.aiCreateReadyStatus === 'cancelled') return 'idle';
  if (state.aiStreamStatus === 'idle' && state.aiJobId === null) return 'idle';
  if (state.title.trim().length === 0) return 'starting';
  if (
    state.marketTicker.trim().length === 0 ||
    state.description.trim().length === 0 ||
    state.outcomes.every((outcome) => outcome.label.trim().length === 0)
  ) {
    return 'writingMarket';
  }
  if (state.aiImagesExpected > 0 && state.aiImagesSettled < state.aiImagesExpected) {
    return 'findingImages';
  }
  if (state.aiImagesExpected > 0) return 'finalizingImages';
  return 'writingMarket';
}
